import React, { useCallback, useMemo } from 'react';
import {
  useNotify,
  useRecordContext,
  useUpdate,
  useRefresh,
  Form,
  required,
} from 'react-admin';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Typography,
} from '@mui/material';
import { Cancel } from '@mui/icons-material';
import useDialogStatus from '../hooks/useDialogStatus';
import caseStatuses, { CaseStatuses } from '../constants/caseStatuses';
import useCaseStatusColors from '../hooks/useCaseStatusColors';
import StatusField from './StatusField';
import RadioInput from './RadioInput';
import SmartDateTimeInput from './SmartDateTimeInput';
import dateParse, { dateFormat } from '../components/utils/dateParser';

type CaseDoc = {
  id: string;
  status: CaseStatuses;
  caseDeadline: Date;
};

const UpdateCaseStatusField = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const record = useRecordContext<CaseDoc>();
  const { id, status } = record || {};

  const { open, openDialog, closeDialog } = useDialogStatus();

  const [closed] = useUpdate();

  const handleSubmit = useCallback(({
    status: newStatus,
    caseDeadline,
  }: {
    status?: string;
    caseDeadline?: string;
  }) => {
    closed(
      'cases',
      { id: `${id}/status`, data: { status: newStatus, caseDeadline } },
      {
        onSuccess: () => {
          notify('Case status has been updated');
          closeDialog();
          refresh();
        },
        onError: () => {
          notify('Could not update case status', { type: 'error' });
        },
      },
    );
  }, [closeDialog, closed, id, notify, refresh]);
  const caseStatusColors = useCaseStatusColors();
  const finalChoices = useMemo(() => {
    const allChoices = [
      { id: caseStatuses.OPEN, name: 'Open', color: caseStatusColors[caseStatuses.OPEN] },
      { id: caseStatuses.IN_PROGRESS, name: 'In progress', color: caseStatusColors[caseStatuses.IN_PROGRESS] },
      { id: caseStatuses.PENDING, name: 'Pending', color: caseStatusColors[caseStatuses.PENDING] },
      { id: caseStatuses.CLOSED, name: 'Closed', color: caseStatusColors[caseStatuses.CLOSED] },
    ];
    return allChoices.filter((choice) => choice.id !== status);
  }, [caseStatusColors, status]);

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="xs">
        <DialogTitle fontWeight="bold">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold">Update status</Typography>
            <IconButton onClick={closeDialog}>
              <Cancel />
            </IconButton>
          </Box>
        </DialogTitle>
        <Form
          onSubmit={handleSubmit}
          record={{
            status: record?.status || caseStatuses.OPEN,
            caseDeadline: record?.caseDeadline || undefined,
          }}
        >
          <DialogContent sx={{
            display: 'flex',
            justifyContent: 'start',
            paddingX: '18%',
          }}
          >
            <Box display="flex" gap={5} flexDirection="column">
              <Box>
                <FormLabel focused={false}>Current status</FormLabel>
                <StatusField source="status" />
              </Box>
              <FormControl>
                <FormLabel focused={false}>Change to:</FormLabel>
                <RadioInput
                  source="status"
                  validate={required()}
                  choices={finalChoices}
                />
              </FormControl>
              {record?.caseDeadline && (
                <Box>
                  <FormLabel focused={false}>Case deadline</FormLabel>
                  <SmartDateTimeInput
                    label=""
                    source="caseDeadline"
                    parse={dateParse}
                    format={dateFormat}
                    fullWidth
                  />
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button variant="contained" type="submit">Save</Button>
          </DialogActions>
        </Form>
      </Dialog>
      <Button
        variant="contained"
        onClick={openDialog}
      >
        Update status
      </Button>
    </>
  );
};

export default UpdateCaseStatusField;
